import * as React from "react";
import { useEffect, useState } from "react";
import SeverityChart from "./SeverityChart";
import Scan, { LandscapeDescriptors, PortraitDescriptors } from "../commons/model/Scan";
import AssessmentResultPreviewModel from "../commons/model/report/AssessmentResultPreviewModel";
import { getSeveritySummary } from "../commons/model/treatment/SeveritySummary";
import ModalAlbumViewer from "../commons/components/modal/ModalAlbumViewer";
import { DownloadGalleryPhotosAction } from "../commons/components/AlbumViewer";
import PatientAnswers from "../commons/components/PatientAnswers";
import "./ReportPreview.scss";
import ViewAction from "../commons/components/action/ViewAction";
import ReportTypeLabel from "../label/ReportTypeLabel";
import reportComplexityBar from "../commons/components/ReportComplexityBar";
import FlowType from "../commons/model/FlowType";
import TextAction from "../commons/components/action/TextAction";
import { EditSharingAction, EditSharingOption, ShareReportWithPatientAction } from "../reports/ReportModels";
import ShareReportModal from "../reports/ShareReportModal";
import { dateWithOptionalTime } from "../commons/utils/date";
import VerticalDropdown from "../commons/components/vertical-dropdown/VerticalDropdown";
import { ShareMailIcon } from "../commons/components/images/ShareMailIcon";

export interface AssessmentResultPreviewProps {
  assessmentResultPreview: AssessmentResultPreviewModel;
  actions: AssessmentResultPreviewActions;
  isPatientView?: boolean;
}

interface AssessmentResultPreviewActions {
  downloadGalleryPhotos: DownloadGalleryPhotosAction;
  shareWithPatientAction?: ShareReportWithPatientAction;
  editSharingAction?: EditSharingAction;
}

export default function AssessmentResultPreview({
                                                  assessmentResultPreview,
                                                  actions,
                                                  isPatientView,
                                                }: {
  assessmentResultPreview: AssessmentResultPreviewModel;
  actions: AssessmentResultPreviewActions;
  isPatientView?: boolean;
}) {
  const [showAlbum, setShowAlbum] = useState<boolean>();
  const [selectedPhoto, setSelectedPhoto] = useState<Scan>();

  const formattedDate = dateWithOptionalTime(assessmentResultPreview.creation);
  const [showShareReportModal, setShowShareReportModal] = useState(false);
  const [editSharingOption, setEditSharingOption] = useState<EditSharingOption>(null);
  const photos = [
    ...assessmentResultPreview.scanPackages.closed,
    ...assessmentResultPreview.scanPackages.opened,
  ];

  const smilePhotos = [...assessmentResultPreview.scanPackages.face];

  useEffect(() => window["verticalDropdownUtil"] && window["verticalDropdownUtil"].reAttach());

  const photosView = (photos: Scan[]) => (
    <div className="h-flex no-wrap balanced photos grow">
      {photos.map((photo: Scan) => (
        <div key={photo.uid}>
          <img
            src={photo.href}
            alt={photo.descriptor}
            className="clickable"
            onClick={() => {
              setShowAlbum(true);
              setSelectedPhoto(photo);
            }}
          />
        </div>
      ))}
    </div>
  );

  const showShareReport = () => {
    // prevent double scrollbar
    document.body.style.overflow = 'hidden'
    setShowShareReportModal(true)
  }

  const hideShareReport = () => {
    document.body.style.overflow = 'auto'
    setShowShareReportModal(false)
  }

  const onEditSharingUpdate = (newEditSharingOption) => {
    setEditSharingOption(newEditSharingOption)
  }

  return (
    <>
      <div
        id={"assessment-result-preview"}
        className={(isPatientView ? "" : "with-shadow panel") + " assessment-result-preview"}
      >
        <div className="h-flex space-between">
          <div className="panel-title with-margins">
            <span>
              {isPatientView
                ? i18n["Page.Portal.PatientDashboard.LatestReport.Title"]
                : i18n["Page.PatientManagement.LastReport.Title"]}
            </span>
            <ReportTypeLabel reportType={assessmentResultPreview.type} />
          </div>
          <div className={"v-flex patient-report-card"}>
            <ViewAction
              href={assessmentResultPreview.url}
              label={
                isPatientView
                  ? i18n["Page.Portal.PatientDashboard.ViewReport.Action"]
                  : i18n["Activity.Report.Action.ViewReport"]
              }
              additionalClass={"margin-bottom"}
            />
            {!isPatientView && actions.shareWithPatientAction &&
              <TextAction
                key="share-report-modal"
                onClick={showShareReport}
                icon={<ShareMailIcon id="share-mail-icon" hoverable={false}/>}
                label={i18n["Page.AssessmentResult.Action.ShareReport"]}
              />
            }
          </div>
        </div>
        <div className="h-flex with-margins">
          <div className="h-flex with-separator">
            <span>
              {formattedDate}{" "}
              {isPatientView &&
                assessmentResultPreview.practiceName &&
                i18n["Page.Portal.PatientDashboard.At"] + assessmentResultPreview.practiceName}
            </span>
            {assessmentResultPreview.reportComplexity != null &&
              assessmentResultPreview.reportComplexity != undefined &&
              assessmentResultPreview.type != FlowType.SMILE_SIMULATION && (
                <div className={"h-flex with-margins"}>
                  <span>{i18n["Page.PatientManagement.LastReport.Complexity"]}</span>
                  {reportComplexityBar(assessmentResultPreview.reportComplexity)}
                </div>
              )}
          </div>
          {!isPatientView && !assessmentResultPreview.isRead && (
            <span className="label primary">
              <span>{i18n["Global.New"]}</span>
            </span>
          )}
        </div>
        <div className="h-flex with-separator no-wrap photos-panel">
          {assessmentResultPreview.observations &&
            assessmentResultPreview.observations.length > 0 && (
              <div>
                <SeverityChart
                  pieSize={64}
                  summary={getSeveritySummary(assessmentResultPreview.observations)}
                />
              </div>
            )}
          {photos.length > 0 && photosView(photos)}
          {smilePhotos.length > 0 && photosView(smilePhotos)}
        </div>
        {assessmentResultPreview.patientAnswers.length > 0 && (
          <VerticalDropdown
            customButton={
              <div className="h-flex clickable">
                <span>
                  {isPatientView
                    ? i18n["Page.Portal.PatientDashboard.PatientExpectation.Title"]
                    : i18n["Page.AssessmentResult.PatientExpectation.Title"]}
                </span>
                <i className="ic_chevron_down" />
              </div>
            }
          >
            <PatientAnswers answers={assessmentResultPreview.patientAnswers} />
          </VerticalDropdown>
        )}
      </div>
      {showAlbum && (
        <ModalAlbumViewer
          hide={() => setShowAlbum(false)}
          portraitPhotos={[...assessmentResultPreview.galleryPhotos].filter((scan) =>
            PortraitDescriptors.includes(scan.descriptor),
          )}
          landscapePhotos={[...assessmentResultPreview.galleryPhotos].filter((scan) =>
            LandscapeDescriptors.includes(scan.descriptor),
          )}
          action={actions.downloadGalleryPhotos}
          selectedPhoto={selectedPhoto}
        />
      )}

      {!isPatientView && actions.shareWithPatientAction && showShareReportModal && (
        <ShareReportModal
          hide={hideShareReport}
          onEditSharingUpdate={onEditSharingUpdate}
          reportUid={assessmentResultPreview.reportUid}
          shareAction={actions.shareWithPatientAction}
          reportType={assessmentResultPreview.type}
          editSharingAction={{
            ...actions.editSharingAction,
            ...editSharingOption,
          }}
        />
      )}
    </>
  );
}
